import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // CRITICAL: Prevent double processing (React StrictMode runs effects twice in dev)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id in URL');
          navigate('/login');
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        console.log('[AuthCallback] Processing session_id:', sessionId.substring(0, 10) + '...');
        console.log('[AuthCallback] User-Agent:', navigator.userAgent);
        console.log('[AuthCallback] Is Mobile Safari:', /iPhone|iPad|iPod/.test(navigator.userAgent));

        // Exchange session_id for user data + session_token
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        console.log('[AuthCallback] Calling:', `${apiUrl}/api/auth/session`);
        
        const response = await fetch(`${apiUrl}/api/auth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Important: Send/receive cookies
          body: JSON.stringify({ session_id: sessionId })
        });

        console.log('[AuthCallback] Response status:', response.status);
        console.log('[AuthCallback] Response headers:', [...response.headers.entries()]);

        if (!response.ok) {
          const error = await response.json();
          console.error('[AuthCallback] Session exchange failed:', error);
          
          // Check if it's a 403 (not on allowlist)
          if (response.status === 403) {
            // Redirect to landing page with beta message
            window.location.href = '/?beta_access_required=true';
            return;
          }
          
          navigate('/login');
          return;
        }

        const data = await response.json();
        console.log('[AuthCallback] Session exchange successful for:', data.user.email);

        // Per Emergent Auth playbook: navigate straight to dashboard with user data in state.
        // Passing `user` via location.state lets Dashboard skip the /auth/me roundtrip on first render,
        // avoiding any race where the cookie isn't yet readable by a fresh fetch on iOS Safari.
        navigate('/dashboard', {
          replace: true,
          state: { user: data.user },
        });

      } catch (error) {
        console.error('[AuthCallback] Auth callback error:', error);
        navigate('/login');
      }
    };

    processSession();
    // Intentional mount-only: hasProcessed ref guards against double-run in StrictMode.
    // Adding all processSession inner deps would defeat the idempotency guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}
